<template>
  <div class="animated fadeIn">
    <div class="dashboard-header d-flex justify-content-end mb-3">
      <display-mode-toggle
        v-model="displayMode"
        storage-key="dashboard:displayMode:ead"
      />
    </div>
    <!-- Cards de Estatísticas -->
    <b-card class="metrics-container">
      <div class="metrics-grid">
        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i :class="firstMetricIcon"></i>
            </div>
            <p class="metric-label">{{ firstMetricLabel }}</p>
          </div>
          <h3 class="metric-value">{{ formatMetric(firstMetricValue) }}</h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: firstMetricProgress + '%' }"
            ></div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i class="fa fa-users"></i>
            </div>
            <p class="metric-label">ALUNOS MATRICULADOS</p>
          </div>
          <h3 class="metric-value">
            {{ formatMetric(stats.totalStudents, stats.totalEnrollments) }}
          </h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: studentsProgress + '%' }"
            ></div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i class="fa fa-book"></i>
            </div>
            <p class="metric-label">TOTAL DE MATRÍCULAS</p>
          </div>
          <h3 class="metric-value">{{ stats.totalEnrollments }}</h3>
          <!-- TOTAL DE MATRÍCULAS é o denominador, mantém absoluto -->
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: enrollmentsProgress + '%' }"
            ></div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i class="fa fa-check-circle"></i>
            </div>
            <p class="metric-label">TAXA DE CONCLUSÃO</p>
          </div>
          <h3 class="metric-value">{{ stats.completionRate }}%</h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: completionProgress + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Atalhos Rápidos -->
    <b-row class="mt-4">
      <b-col>
        <b-card no-body class="dashboard-card">
          <b-card-body>
            <h5 class="mb-4">Ações Rápidas</h5>
            <b-row>
              <b-col md="3" class="mb-3">
                <b-button
                  variant="outline-primary"
                  size="lg"
                  block
                  @click="$router.push('/ead/cursos')"
                  class="action-button"
                >
                  <i class="fa fa-graduation-cap fa-2x d-block mb-2"></i>
                  Gerenciar Cursos
                </b-button>
              </b-col>
              <b-col md="3" class="mb-3">
                <b-button
                  variant="outline-primary"
                  size="lg"
                  block
                  @click="$router.push('/ead/turmas')"
                  class="action-button"
                >
                  <i class="fa fa-users fa-2x d-block mb-2"></i>
                  Gerenciar Turmas
                </b-button>
              </b-col>
              <b-col v-if="!isCompanyRoot" md="3" class="mb-3">
                <b-button
                  variant="outline-primary"
                  size="lg"
                  block
                  @click="$router.push('/ead/matricular')"
                  class="action-button"
                >
                  <i class="fa fa-user-plus fa-2x d-block mb-2"></i>
                  Matricular Alunos
                </b-button>
              </b-col>
              <b-col md="3" class="mb-3">
                <b-button
                  variant="outline-info"
                  size="lg"
                  block
                  @click="$router.push('/ead/usuarios')"
                  class="action-button"
                >
                  <i class="fa fa-users fa-2x d-block mb-2"></i>
                  Usuários EAD
                </b-button>
              </b-col>
              <b-col md="3" class="mb-3">
                <b-button
                  variant="outline-warning"
                  size="lg"
                  block
                  @click="$router.push('/ead/relatorios')"
                  class="action-button"
                >
                  <i class="fa fa-chart-bar fa-2x d-block mb-2"></i>
                  Relatórios
                </b-button>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Cursos Recentes e Atividade -->
    <b-row class="mt-4">
      <b-col md="8">
        <b-card no-body class="dashboard-card">
          <b-card-body>
            <h5 class="mb-3">Cursos Recentes</h5>
            <b-table
              :items="recentCourses"
              :fields="courseFields"
              :busy="loading"
              responsive
              hover
              show-empty
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner small></b-spinner>
                </div>
              </template>

              <template #empty>
                <div class="text-center text-muted py-3">
                  Nenhum curso cadastrado
                </div>
              </template>

              <template #cell(nome)="data">
                <strong>{{ data.value }}</strong>
              </template>

              <template #cell(categoria)="data">
                <b-badge variant="primary" pill>{{ data.value }}</b-badge>
              </template>

              <template #cell(createdAt)="data">
                {{ formatDate(data.value) }}
              </template>

              <template #cell(actions)="data">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="$router.push(`/ead/cursos/${data.item._id}/editar`)"
                >
                  <i class="fa fa-cog"></i>
                  Configurar
                </b-button>
              </template>
            </b-table>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col md="4">
        <b-card no-body class="dashboard-card">
          <b-card-body>
            <h5 class="mb-3">Avisos Importantes</h5>
            <b-list-group flush>
              <b-list-group-item
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <i class="fa fa-info-circle text-info mr-2"></i>
                  Sistema EAD integrado
                </div>
                <b-badge variant="success" pill>Ativo</b-badge>
              </b-list-group-item>
              <b-list-group-item
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <i class="fa fa-users text-primary mr-2"></i>
                  Alunos ativos
                </div>
                <b-badge variant="primary" pill>{{
                  stats.totalStudents
                }}</b-badge>
              </b-list-group-item>
              <b-list-group-item
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <i class="fa fa-clock text-warning mr-2"></i>
                  Matrículas pendentes
                </div>
                <b-badge variant="warning" pill>{{
                  stats.pendingEnrollments
                }}</b-badge>
              </b-list-group-item>
            </b-list-group>

            <hr />

            <div class="text-center">
              <p class="text-muted small mb-2">
                <i class="fa fa-lightbulb mr-1"></i>
                Dica: Use a página de relatórios para acompanhar o progresso dos
                alunos
              </p>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="$router.push('/ead/relatorios')"
              >
                Ver Relatórios
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { getCourses, getEnrollments, getEadUsers } from "@/api/ead";
import moment from "moment";
import DisplayModeToggle from "../dashboard/DisplayModeToggle.vue";

const STORAGE_KEY = "dashboard:displayMode:ead";

export default {
  name: "EadDashboard",
  components: {
    DisplayModeToggle,
  },
  data() {
    const stored = localStorage.getItem(STORAGE_KEY);
    return {
      displayMode: stored === "percent" ? "percent" : "absolute",
      loading: true,
      stats: {
        totalCourses: 0,
        totalStudents: 0,
        totalEnrollments: 0,
        completionRate: 0,
        pendingEnrollments: 0,
        coursesInProgress: 0,
      },
      recentCourses: [],
      courseFields: [
        { key: "nome", label: "Nome" },
        { key: "categoria", label: "Categoria" },
        { key: "createdAt", label: "Criado em" },
        { key: "actions", label: "Ações" },
      ],
    };
  },
  computed: {
    isCompanyRoot() {
      return this.$store.getters.role === "company-root";
    },
    firstMetricIcon() {
      return this.isCompanyRoot ? "fa fa-spinner" : "fa fa-graduation-cap";
    },
    firstMetricLabel() {
      return this.isCompanyRoot ? "CURSOS EM ANDAMENTO" : "TOTAL DE CURSOS";
    },
    firstMetricValue() {
      return this.isCompanyRoot
        ? this.stats.coursesInProgress
        : this.stats.totalCourses;
    },
    firstMetricProgress() {
      if (this.isCompanyRoot) {
        const total = this.stats.coursesInProgress;
        return total > 0 ? Math.min((total / 30) * 100, 100) : 0;
      }
      return this.coursesProgress;
    },
    coursesProgress() {
      const total = this.stats.totalCourses;
      return total > 0 ? Math.min((total / 50) * 100, 100) : 0;
    },
    studentsProgress() {
      const total = this.stats.totalStudents;
      return total > 0 ? Math.min((total / 100) * 100, 100) : 0;
    },
    enrollmentsProgress() {
      const total = this.stats.totalEnrollments;
      return total > 0 ? Math.min((total / 150) * 100, 100) : 0;
    },
    completionProgress() {
      return this.stats.completionRate;
    },
  },
  mounted() {
    this.loadDashboardData();
  },
  methods: {
    formatMetric(value, denominator) {
      if (this.displayMode !== "percent" || denominator === undefined) {
        return value;
      }
      const numeric = typeof value === "number" ? value : parseFloat(value);
      if (!denominator || denominator <= 0 || Number.isNaN(numeric)) {
        return "0,00%";
      }
      const pct = (numeric / denominator) * 100;
      return `${pct.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    },
    async loadDashboardData() {
      this.loading = true;
      try {
        // Buscar cursos
        const coursesRes = await getCourses();
        const courses = coursesRes.data || [];
        this.stats.totalCourses = courses.length;
        this.recentCourses = courses.slice(0, 5); // Últimos 5 cursos

        // Buscar usuários (alunos)
        try {
          const usersRes = await getEadUsers();
          const users = usersRes.data || [];
          // totalStudents será calculado a partir das matrículas (alunos únicos matriculados)
        } catch (error) {
          console.warn("Erro ao carregar usuários:", error);
        }

        // Buscar matrículas
        try {
          const enrollmentsRes = await getEnrollments();
          const enrollments = enrollmentsRes.data || [];
          this.stats.totalEnrollments = enrollments.length;

          // Calcular alunos únicos matriculados
          const uniqueStudents = new Set(
            enrollments.map((e) => e.userId?._id || e.userId)
          );
          this.stats.totalStudents = uniqueStudents.size;

          // Calcular cursos em andamento (cursos com matrículas ativas)
          const activeCourses = new Set(
            enrollments
              .filter((e) => e.status === "active" && !e.completed)
              .map((e) => e.courseId?._id || e.courseId)
          );
          this.stats.coursesInProgress = activeCourses.size;

          // Calcular taxa de conclusão
          const completed = enrollments.filter((e) => e.completed).length;
          this.stats.completionRate =
            enrollments.length > 0
              ? Math.round((completed / enrollments.length) * 100)
              : 0;

          // Matrículas pendentes (ativas mas não concluídas)
          this.stats.pendingEnrollments = enrollments.filter(
            (e) => e.status === "active" && !e.completed
          ).length;
        } catch (error) {
          console.warn("Erro ao carregar matrículas:", error);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do dashboard:", error);
        this.$bvToast.toast("Erro ao carregar dados do dashboard", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "N/A";
    },
  },
};
</script>

<style scoped>
/* Metrics Container */
.metrics-container {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
  margin-bottom: 2rem;
}

.dashboard-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}

.metrics-grid {
  display: flex;
  align-items: stretch;
  min-height: 120px;
}

.metric-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 1.5rem 1rem;
  position: relative;
}

.divider {
  width: 1px;
  background: repeating-linear-gradient(
    to bottom,
    #cbd5e0 0px,
    #cbd5e0 6px,
    transparent 6px,
    transparent 12px
  );
  margin: 0;
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.metric-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #000000;
}

.metric-value {
  font-size: 2rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: #000000;
  line-height: 1;
}

.metric-label {
  font-size: 0.85rem;
  color: #000000;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.progress-container {
  width: 75%;
  height: 5px;
  background-color: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #4299e1;
  border-radius: 2px;
  transition: width 0.4s ease;
}

/* Action Buttons */
.action-button {
  border-width: 2px;
  transition: all 0.3s ease;
  height: 100%;
  padding: 1.5rem 1rem;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.action-button i {
  font-size: 2rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .metrics-grid {
    flex-direction: column;
    min-height: auto;
  }

  .divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .metric-section {
    padding: 1rem;
  }

  .metric-icon {
    width: 22px;
    height: 22px;
    font-size: 14px;
  }

  .metric-value {
    font-size: 1.75rem;
  }

  .metric-label {
    font-size: 0.8rem;
  }

  .action-button {
    padding: 1rem 0.75rem;
  }

  .action-button i {
    font-size: 1.5rem;
  }
}

/* Table Styles */
.table {
  margin-bottom: 0;
}

.table td {
  vertical-align: middle;
}

/* List Group Styles */
.list-group-item {
  border-left: none;
  border-right: none;
  padding-left: 0;
  padding-right: 0;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:last-child {
  border-bottom: none;
}
</style>
